import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { Box, Card, CardContent, Chip, Stack, Typography, useTheme } from '@mui/material';
import { FC, useMemo } from 'react';
import { BookingStatsConfig } from 'utils/validators/HelperFunctions';
import { STATUS_COLORS } from '../Reports/echartsConfig';
import DonutChart from './DonutChart';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';

const formatINR = (value: number) => formatCurrencyWithSymbol(value);

const KPI_COLUMN_WIDTH = 220;
const BREAKDOWN_COLUMN_WIDTH = 300;

const sectionTitleSx = {
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.6,
  color: 'text.secondary',
  mb: 1.25,
  flexShrink: 0,
};

const columnShellSx = {
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  minHeight: 0,
};

const columnBodySx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column' as const,
};

type BookingCountItem = {
  name: string;
  value: number;
  amount?: number;
};

interface BookingsSummaryCardProps {
  bookingCounts: any[];
  isLoading: boolean;
  onRowClick?: (name: string) => void;
  hideRfq?: boolean;
}

const ORDER: string[] = ['CONFIRMED', 'TENTATIVE', 'WAITLISTED', 'CANCELLED', 'PAYMENT_DUE', 'RFQ'];

const BookingsSummaryCard: FC<BookingsSummaryCardProps> = ({
  bookingCounts,
  onRowClick,
  hideRfq = false,
}) => {
  const theme = useTheme();

  const rows: BookingCountItem[] = useMemo(() => {
    const list = Array.isArray(bookingCounts) ? bookingCounts : [];
    const byName = new Map<string, any>();
    for (const item of list) {
      if (item?.name != null) byName.set(String(item.name), item);
    }
    return ORDER.filter(k => k !== 'RFQ' || !hideRfq).map(name => {
      const item = byName.get(name);
      return {
        name,
        value: typeof item?.value === 'number' ? item.value : Number(item?.value) || 0,
        amount: typeof item?.amount === 'number' ? item.amount : item?.amount != null ? Number(item.amount) : undefined,
      };
    });
  }, [bookingCounts, hideRfq]);

  const colorMap: { [key: string]: string } = useMemo(
    () => ({
      CONFIRMED: STATUS_COLORS.confirmed,
      TENTATIVE: STATUS_COLORS.tentative,
      WAITLISTED: STATUS_COLORS.waitlisted,
      CANCELLED: STATUS_COLORS.cancelled,
      PAYMENT_DUE: STATUS_COLORS.paymentDue,
      RFQ: STATUS_COLORS.rfq,
    }),
    [],
  );

  const totalBookings = useMemo(() => rows.reduce((sum, r) => sum + r.value, 0), [rows]);

  const pieItems = useMemo(() => {
    return rows
      .filter(r => r.value > 0)
      .map(r => ({
        statusKey: r.name,
        label: BookingStatsConfig(r.name).label,
        value: r.value,
        color: colorMap[r.name] || '#94a3b8',
        amount: r.amount,
      }));
  }, [rows, colorMap]);

  const totalRevenue = useMemo(() => rows.find(r => r.name === 'CONFIRMED')?.amount || 0, [rows]);
  const totalPending = useMemo(() => rows.find(r => r.name === 'PAYMENT_DUE')?.amount || 0, [rows]);
  const totalPotential = useMemo(() => {
    const tentative = rows.find(r => r.name === 'TENTATIVE');
    const waitlisted = rows.find(r => r.name === 'WAITLISTED');
    return (tentative?.amount || 0) + (waitlisted?.amount || 0);
  }, [rows]);

  const kpiItems = [
    {
      key: 'revenue',
      label: 'Revenue',
      hint: 'Confirmed bookings',
      value: totalRevenue,
      icon: MonetizationOnOutlinedIcon,
      accent: theme.palette.success.main,
      gradient: 'linear-gradient(135deg, rgba(240, 253, 244, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
      border: '#bbf7d0',
      iconBg: 'rgba(22, 163, 74, 0.12)',
    },
    {
      key: 'pending',
      label: 'Pending',
      hint: 'Payment due',
      value: totalPending,
      icon: PendingActionsOutlinedIcon,
      accent: theme.palette.warning.main,
      gradient: 'linear-gradient(135deg, rgba(255, 247, 237, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
      border: '#fed7aa',
      iconBg: 'rgba(234, 88, 12, 0.12)',
    },
    {
      key: 'potential',
      label: 'Potential',
      hint: 'Tentative + waitlisted',
      value: totalPotential,
      icon: TrendingUpOutlinedIcon,
      accent: theme.palette.primary.main,
      gradient: 'linear-gradient(135deg, rgba(239, 246, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
      border: '#bfdbfe',
      iconBg: 'rgba(37, 99, 235, 0.12)',
    },
  ];

  return (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
      <CardContent
        sx={{
          p: { xs: 2, md: 2.5 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { pb: { xs: 2, md: 2.5 } },
        }}>
        <Box
          sx={{
            flex: 1,
            width: '100%',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: `minmax(0, 1fr) minmax(${BREAKDOWN_COLUMN_WIDTH}px, 1.6fr) ${KPI_COLUMN_WIDTH}px`,
            },
            alignItems: 'stretch',
            gap: { xs: 2, md: 1 },
          }}>
          {/* Column 1 — Chart (left-aligned) */}
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              minWidth: 0,
              alignSelf: 'stretch',
            }}>
            <Box sx={columnShellSx}>
              <Typography sx={sectionTitleSx}>Overview</Typography>
              <Box
                sx={{
                  ...columnBodySx,
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  containerType: 'size',
                  minWidth: { md: 280 },
                  minHeight: { md: 280 },
                }}>
                <Box
                  sx={{
                    width: 'min(100cqw, 100cqh)',
                    aspectRatio: '1',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    flexShrink: 0,
                  }}>
                  <DonutChart
                    fill
                    items={pieItems}
                    centerLabel={String(totalBookings)}
                    centerSub="Total Bookings"
                    onSegmentClick={onRowClick}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Column 2 — Breakdown (fills middle) */}
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              display: 'flex',
              alignSelf: 'stretch',
            }}>
            <Box sx={{ ...columnShellSx, width: '100%', alignItems: 'center' }}>
              <Typography sx={{ ...sectionTitleSx, textAlign: 'center', width: '100%' }}>Breakdown</Typography>
              <Stack
                spacing={0.75}
                sx={{
                  ...columnBodySx,
                  justifyContent: 'center',
                  width: '100%',
                }}>
              {rows.map(row => {
                const { label } = BookingStatsConfig(row.name);
                const canClick = row.value > 0 && !!onRowClick;
                const rowColor = colorMap[row.name] || '#94a3b8';
                const sharePct = totalBookings > 0 ? Math.round((row.value / totalBookings) * 100) : 0;

                return (
                  <Box
                    key={row.name}
                    onClick={() => {
                      if (canClick && onRowClick) onRowClick(row.name);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1.25,
                      py: 0.9,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: row.value > 0 ? 'divider' : 'transparent',
                      bgcolor: row.value > 0 ? '#FAFAFA' : 'transparent',
                      opacity: row.value > 0 ? 1 : 0.5,
                      cursor: canClick ? 'pointer' : 'default',
                      transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
                      ...(canClick && {
                        '&:hover': {
                          borderColor: rowColor,
                          bgcolor: `${rowColor}12`,
                          boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
                        },
                      }),
                    }}>
                    <Box
                      sx={{
                        width: 4,
                        alignSelf: 'stretch',
                        minHeight: 36,
                        borderRadius: 1,
                        bgcolor: rowColor,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }} noWrap>
                        {label}
                      </Typography>
                      {totalBookings > 0 && row.value > 0 ? (
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600, mt: 0.2 }}>
                          {sharePct}% of total
                        </Typography>
                      ) : null}
                    </Box>
                    <Chip
                      label={row.value}
                      size="small"
                      sx={{
                        height: 26,
                        minWidth: 36,
                        fontWeight: 800,
                        fontSize: 12.5,
                        bgcolor: `${rowColor}18`,
                        color: rowColor,
                        border: `1px solid ${rowColor}33`,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.15,
                        px: 0.75,
                        py: 0.35,
                        borderRadius: 1,
                        bgcolor: canClick ? 'primary.main' : 'action.disabledBackground',
                        color: canClick ? 'primary.contrastText' : 'text.disabled',
                        flexShrink: 0,
                        transition: 'opacity 0.15s ease',
                        ...(canClick && {
                          '&:hover': { opacity: 0.92 },
                        }),
                      }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 800, userSelect: 'none', lineHeight: 1 }}>
                        View
                      </Typography>
                      <ChevronRightRoundedIcon sx={{ fontSize: 15 }} />
                    </Box>
                  </Box>
                );
              })}
              </Stack>
            </Box>
          </Box>

          {/* Column 3 — KPIs (right column) */}
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              display: 'flex',
              alignSelf: 'stretch',
            }}>
            <Box sx={{ ...columnShellSx, alignItems: 'flex-end', width: '100%' }}>
              <Typography sx={{ ...sectionTitleSx, textAlign: 'right', width: '100%' }}>Summary</Typography>
              <Stack spacing={1} sx={{ ...columnBodySx, height: '100%', width: '100%', justifyContent: 'center' }}>
              {kpiItems.map(kpi => {
                const Icon = kpi.icon;
                return (
                  <Box
                    key={kpi.key}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 1.1,
                      borderRadius: 1.5,
                      border: `1px solid ${kpi.border}`,
                      background: kpi.gradient,
                      boxShadow: '0 1px 6px rgba(15, 23, 42, 0.04)',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.07)',
                      },
                    }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: kpi.iconBg,
                        color: kpi.accent,
                        flexShrink: 0,
                        border: `1px solid ${kpi.border}`,
                      }}>
                      <Icon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          color: 'text.secondary',
                          lineHeight: 1.2,
                        }}>
                        {kpi.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 14.5,
                          fontWeight: 800,
                          color: kpi.accent,
                          mt: 0.25,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontVariantNumeric: 'tabular-nums',
                          lineHeight: 1.15,
                        }}>
                        {formatINR(kpi.value)}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600, mt: 0.2 }} noWrap>
                        {kpi.hint}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              </Stack>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BookingsSummaryCard;
